import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import './index.css'; // Ensure index.css is imported
import { hasPermission } from './utils/permissions';
import { NotificationProvider, NotificationManager } from './components/common';

// Layout components
import Dashboard from './components/layout/Dashboard';
import Login from './components/auth/Login';
import CompanySignup from './components/auth/CompanySignup'; // Import CompanySignup
import Register from './components/auth/Register';
import SetPassword from './components/auth/SetPassword'; // Import SetPassword for invite flow
import ForgotPassword from './components/auth/ForgotPassword'; // Import ForgotPassword
import ResetPassword from './components/auth/ResetPassword'; // Import ResetPassword
import PrivateRoute, { PermissionRestrictedRoute } from './components/routing/PrivateRoute';

// Main components
import LeadView from './components/leads/LeadView';
import LeadsListWithShared from './components/leads/LeadsListWithShared';
import DealsList from './components/deals/DealsList';
import PipelineView from './components/deals/PipelineView';
import CalendarView from './components/calendar/CalendarView';
import Settings from './components/settings/Settings';
import KPIsView from './components/kpis/KPIsView';
import MailerView from './components/mailer/MailerView';
import EmployeesList from './components/employees/EmployeesList';
import ChatRoom from './components/chat/ChatRoom';
import TasksList from './components/tasks/TasksList';
// TermsAndConditions is likely handled elsewhere or not needed at top level
// import TermsAndConditions from './components/auth/TermsAndConditions';
import OnboardingWizard from './components/onboarding/OnboardingWizard'; // Import OnboardingWizard
import CompanyList from './components/platform/CompanyList'; // Import Platform Admin Company List
import BrregExplorer from './components/brreg/BrregExplorer'; // Import Brreg Explorer (legacy)
import { VideoCoursesList, VideoCoursesManager } from './components/video-courses'; // Import Video Courses components

// Import Prospector components
import Prospector from './pages/Prospector'; // Import the Prospector search page
import ProspectorDetail from './pages/ProspectorDetail'; // Import the Prospector detail page
import ProspectorLists from './pages/ProspectorLists'; // Import the Prospector lists page

// Component to handle home page redirection based on permissions
const HomeRedirect = () => {
  const navigate = useNavigate();
  const [isCheckingAuth, setIsCheckingAuth] = React.useState(true);

  React.useEffect(() => {
    // Check if user data is in localStorage. If not, wait briefly.
    // This is a simple mechanism; a more robust solution might use context/state management.
    const checkAuth = () => {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        setIsCheckingAuth(false); // User data found, proceed with permission checks
        // Try to find a suitable page to redirect to based on permissions
        if (hasPermission('leads', 'access')) {
          navigate('/leads');
        } else if (hasPermission('deals', 'access')) {
          navigate('/deals');
        } else if (hasPermission('calendar', 'access')) {
          navigate('/calendar');
        } else if (hasPermission('tasks', 'access')) {
          navigate('/tasks');
        } else if (hasPermission('videos', 'access')) {
          navigate('/video-courses');
        } else if (hasPermission('prospector', 'access')) {
          navigate('/prospector');
        } else if (hasPermission('employees', 'access')) {
          navigate('/employees');
        } else if (hasPermission('settings', 'access')) {
          navigate('/settings');
        } else {
          // Fallback if no primary navigation is accessible
          console.log('No navigation permissions - limited access');
          // Stay on the fallback page (rendered below)
        }
      } else {
        // User data not yet available, maybe still loading? Check again shortly.
        // This timeout is a basic way to handle the race condition.
        console.log('[HomeRedirect] User data not found in localStorage, waiting...');
        setTimeout(checkAuth, 200); // Check again after 200ms
      }
    };

    checkAuth();

  }, [navigate]); // Dependency array includes navigate

  // Show loading indicator while checking auth/waiting for user data
  if (isCheckingAuth) {
    return (
       <div className="flex justify-center items-center h-screen">
         <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
       </div>
    );
  }

  // Render fallback UI if no permissions allow redirection
  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <h1 className="text-2xl font-bold mb-4">Welcome to OmniHub CRM</h1>
      <p className="text-gray-600">Your account has limited access. Please contact an administrator.</p>
    </div>
  );
};

function App() {
  return (
    <NotificationProvider>
      <Router>
        <div className="min-h-screen">
          <NotificationManager />
          <Routes>
          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/company-signup" element={<CompanySignup />} /> {/* Add Company Signup Route */}
          {/* Registration disabled */}
          <Route path="/register" element={<Navigate to="/login" />} />
          {/* Set Password Route for employee invites */}
          <Route path="/set-password" element={<SetPassword />} />
          {/* Forgot & Reset Password Routes */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Onboarding Route - Protected */}
          <Route path="/onboarding" element={<PrivateRoute><OnboardingWizard /></PrivateRoute>} />

          {/* Dashboard Layout with Private Routes */}
          <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>}>
            {/* Platform Admin Route - Moved inside Dashboard layout */}
            {/* Note: Access control is primarily handled within the component/API */}
            <Route path="/platform/companies" element={<CompanyList />} />
            {/* Redirect the root path based on permissions (will be adjusted later for onboarding check) */}
            <Route index element={
              <HomeRedirect />
            } />
            <Route path="leads" element={
              <PermissionRestrictedRoute category="leads" permission="access">
                <LeadsListWithShared />
              </PermissionRestrictedRoute>
            } />
            <Route path="leads/:id" element={
              <PermissionRestrictedRoute category="leads" permission="access">
                <LeadView />
              </PermissionRestrictedRoute>
            } />
            <Route path="deals" element={
              <PermissionRestrictedRoute category="deals" permission="access">
                <DealsList />
              </PermissionRestrictedRoute>
            } />
            <Route path="calendar" element={
              <PermissionRestrictedRoute category="calendar" permission="access">
                <CalendarView />
              </PermissionRestrictedRoute>
            } />
            
            {/* Permission protected routes */}
            <Route path="kpis" element={
              <PermissionRestrictedRoute category="kpis" permission="access">
                <KPIsView />
              </PermissionRestrictedRoute>
            } />
            
            <Route path="employees" element={
              <PermissionRestrictedRoute category="employees" permission="access">
                <EmployeesList />
              </PermissionRestrictedRoute>
            } />
            
            <Route path="email-marketing" element={
              <PermissionRestrictedRoute category="mailer" permission="access">
                <MailerView />
              </PermissionRestrictedRoute>
            } />
            
            <Route path="chat" element={
              <PermissionRestrictedRoute category="chat" permission="access">
                <ChatRoom />
              </PermissionRestrictedRoute>
            } />
            
            <Route path="tasks" element={
              <PermissionRestrictedRoute category="tasks" permission="access">
                <TasksList />
              </PermissionRestrictedRoute>
            } />
            
            {/* Settings route with permission check */}
            <Route path="settings" element={
              <PermissionRestrictedRoute category="settings" permission="access">
                <Settings />
              </PermissionRestrictedRoute>
            } />

            {/* Prospector Routes - with permission protection */}
            <Route path="prospector" element={
              <PermissionRestrictedRoute category="prospector" permission="access">
                <Prospector />
              </PermissionRestrictedRoute>
            } />
            <Route path="prospector/lists" element={
              <PermissionRestrictedRoute category="prospector" permission="access">
                <ProspectorLists />
              </PermissionRestrictedRoute>
            } />
            <Route path="prospector/lists/:listId" element={
              <PermissionRestrictedRoute category="prospector" permission="access">
                <ProspectorLists />
              </PermissionRestrictedRoute>
            } />
            <Route path="prospector/:orgNumber" element={
              <PermissionRestrictedRoute category="prospector" permission="access">
                <ProspectorDetail />
              </PermissionRestrictedRoute>
            } />

            {/* Redirect Brreg Explorer to Prospector - with permission check */}
            <Route path="brreg-explorer" element={
              hasPermission('prospector', 'access')
                ? <Navigate to="/prospector" replace />
                : <Navigate to="/" replace />
            } />
            
            {/* Video Courses Routes */}
            <Route path="video-courses" element={
              <PermissionRestrictedRoute category="videos" permission="access">
                <VideoCoursesList />
              </PermissionRestrictedRoute>
            } />
            
            <Route path="settings/video-courses" element={
              <PermissionRestrictedRoute category="videos" permission="create">
                <VideoCoursesManager />
              </PermissionRestrictedRoute>
            } />
            
            {/* Terms and Conditions are now handled within Dashboard, so this route is removed */}
            {/* <Route path="terms" element={<TermsAndConditions />} /> */}
            
          </Route>
        </Routes>
      </div>
      </Router>
    </NotificationProvider>
  );
}

export default App;
